import React from 'react';
import { X } from 'lucide-react';

/**
 * Toast Container Component
 * Displays toast notifications in a fixed position
 * @param {Object} props - Component props
 * @param {Array} props.toasts - Array of toast objects
 * @param {Function} props.onRemove - Function to remove a toast by ID
 * @param {Object} props.theme - Theme object with styling properties
 * @param {Object} props.colors - Color palette object
 */
export const ToastContainer = ({ toasts, onRemove, theme, colors }) => {
  const getToastStyles = (type) => {
    const borderColor = type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6';
    const glowColor = type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6';
    
    return {
      background: theme.glass?.background || 'rgba(26, 31, 46, 0.95)',
      backdropFilter: theme.glass?.backdropFilter || 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: theme.glass?.WebkitBackdropFilter || 'blur(12px) saturate(180%)',
      padding: '16px 20px',
      borderRadius: '12px',
      border: `2px solid ${borderColor}`,
      boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px ${glowColor}40`,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '300px',
      maxWidth: '400px',
      marginBottom: '12px',
      animation: 'slideInRight 0.3s ease-out',
      transition: 'all 0.3s ease'
    };
  };

  if (toasts.length === 0) return null;

  return (
    <>
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div 
        className="toast-container"
        style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          pointerEvents: 'none'
        }}
      >
        {toasts.map(toast => (
          <div 
            key={toast.id} 
            style={{ 
              ...getToastStyles(toast.type),
              pointerEvents: 'auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(-4px)';
              e.currentTarget.style.boxShadow = `0 12px 40px rgba(0, 0, 0, 0.4), 0 0 30px ${toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6'}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(0)';
              e.currentTarget.style.boxShadow = `0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px ${toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6'}40`;
            }}
          >
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6',
              boxShadow: `0 0 12px ${toast.type === 'success' ? '#10b981' : toast.type === 'error' ? '#ef4444' : '#3b82f6'}`,
              flexShrink: 0
            }} />
            <span style={{ 
              color: theme.text, 
              fontSize: '14px', 
              fontWeight: '500',
              flex: 1,
              lineHeight: '1.5'
            }}>
              {toast.message}
            </span>
            <button 
              onClick={() => onRemove(toast.id)}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.textSecondary,
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                flexShrink: 0,
                opacity: 0.7
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.7';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

